import React, { useState, useEffect } from "react";
import { useLocalization } from "../../localization/LocalizationContext";
import allApi from "../../../../api/allApi";

function ReportProfile({
  onUpdate,
  reportData = {},
  existingReportNames = [],
}) {
  const { localizationStrings } = useLocalization();
  const [reportName, setReportName] = useState(reportData.reportName || "");
  const [description, setDescription] = useState(reportData.description || "");
  const [emailRecipients, setEmailRecipients] = useState(
    Array.isArray(reportData.emailRecipients)
      ? reportData.emailRecipients.join(", ")
      : reportData.emailRecipients || ""
  );
  const [timePeriod, setTimePeriod] = useState(reportData.timePeriod || "30d");
  const [networkIds, setNetworkIds] = useState([]);
const [networkId, setNetworkId] = useState(
  reportData.networkId || ""
);
  const [reportType, setReportType] = useState(
    reportData.reportType || "Custom Report"
  );
  const [executionPeriod, setExecutionPeriod] = useState(
    reportData.executionPeriod || "Run Once"
  );
  const [organizationLogo, setOrganizationLogo] = useState(
    reportData.organizationLogo || "Use VAR Logo"
  );
  const [emailError, setEmailError] = useState("");
  const [reportNameError, setReportNameError] = useState("");
  const [license, setLicense] = useState("");

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const allowedDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "email.com",
    "vvdn.com",
    "vvdntech.com",
    "vvdntech.in",
  ];

  const validateEmails = (emails) => {
    const emailList = emails.split(",").map((email) => email.trim());

    for (let email of emailList) {
      if (!emailRegex.test(email)) {
        setEmailError(
          localizationStrings?.emailFormatError
            ? localizationStrings.emailFormatError.replace("{email}", email)
            : `"${email}" has an incorrect format.`
        );
        return false;
      }

      const domain = email.split("@")[1].toLowerCase();
      if (!allowedDomains.includes(domain)) {
        setEmailError(
          localizationStrings?.emailDomainError
            ? localizationStrings.emailDomainError.replace("{email}", email)
            : `"${email}" is not from an allowed domain.`
        );
        return false;
      }
    }

    setEmailError("");
    return true;
  };

  useEffect(() => {
  fetchNetworkIds();
}, []);

  const fetchNetworkIds = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest("api/network/getAssociatedNetwork");
      if (
        response &&
        response.status === "OK" &&
        Array.isArray(response.response)
      ) {
        setNetworkIds(
        response.response.map((network) => ({
          network_id: network.network_id,
          network_name: network.network_name,
        }))
      );
      }
    } catch (error) {
      console.error("Error fetching network IDs:", error);
    }
  };

  const validateReportName = (name) => {
    const trimmedName = name.trim().toLowerCase();
    const nameExists = existingReportNames.some(
      (existingName) => existingName.toLowerCase() === trimmedName
    );

    if (nameExists) {
      setReportNameError("This report name already exists. Use another name.");
      return false;
    }
    setReportNameError("");
    return true;
  };

  useEffect(() => {
    if (validateReportName(reportName, existingReportNames)) {
      const data = {
        reportName,
        description,
        emailRecipients: emailRecipients
          .split(",")
          .map((email) => email.trim()),
        timePeriod,
        networkId,
        reportType,
        executionPeriod,
      };
      onUpdate(data);
    }
  }, [
    reportName,
    description,
    emailRecipients,
    timePeriod,
    networkId,
    reportType,
    executionPeriod,
    onUpdate,
    existingReportNames,
  ]);

  useEffect(() => {
    const storedLicense = localStorage.getItem("license");
    setLicense(storedLicense);

    // Only allow Custom Report for Enterprise users
    if (
      storedLicense !== "Enterprise" &&
      reportData.reportType === "Custom Report"
    ) {
      setReportType("Troubleshooting Report");
    }
  }, [reportData.reportType]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailRecipients(value);
    validateEmails(value);
  };

  const handleReportNameChange = (e) => {
    const value = e.target.value;
    setReportName(value);
    validateReportName(value);
  };

  return (
    <div>
      <h4>{localizationStrings?.reportProfile}</h4>
      <p>{localizationStrings?.profileHeaderInfo}</p>

      <div className="row mb-35">
        <div className="col-lg-4">
          <label className="form-label">
            {localizationStrings?.reportName}*
          </label>
          <input
            type="text"
            className="form-control"
            value={reportName}
            onChange={handleReportNameChange}
            onBlur={() => validateReportName(reportName)}
          />
          {reportNameError && <p style={{ color: "red" }}>{reportNameError}</p>}
        </div>
        <div className="col-lg-4">
          <label className="form-label">
            {localizationStrings?.description}
          </label>
          <input
            type="text"
            className="form-control"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <h4 className="mb-3">{localizationStrings?.reportType}</h4>
      <div className="report-radio mb-3">
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="reporttype"
            id="reporttype1"
            checked={reportType === "Custom Report"}
            onChange={() => {
              if (license === "Enterprise") {
                setReportType("Custom Report");
              }
            }}
            disabled={license !== "Enterprise"}
          />
          <label className="form-check-label" htmlFor="reporttype1">
            {localizationStrings?.customReport}
            {license !== "Enterprise" && (
              <span className="text-muted ms-2">(Enterprise only)</span>
            )}
          </label>
        </div>
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="reporttype"
            id="reporttype2"
            checked={reportType === "Troubleshooting Report"}
            onChange={() => setReportType("Troubleshooting Report")}
          />
          <label className="form-check-label" htmlFor="reporttype2">
            {localizationStrings?.troubleshootingReport}
          </label>
        </div>
      </div>

      <h4>{localizationStrings?.executionPeriod}</h4>
      <div className="report-radio mb-35">
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="reporttime"
            id="reporttime1"
            checked={executionPeriod === "Run Once"}
            onChange={() => setExecutionPeriod("Run Once")}
          />
          <label className="form-check-label" htmlFor="reporttime1">
            {localizationStrings?.runOnce}
          </label>
        </div>
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="reporttime"
            id="reporttime2"
            checked={executionPeriod === "Scheduled"}
            onChange={() => setExecutionPeriod("Scheduled")}
          />
          <label className="form-check-label" htmlFor="reporttime2">
            {localizationStrings?.scheduled}
          </label>
        </div>
      </div>

     <div className="row mb-35">
  {/* Time Period */}
  <div className="col-lg-4">
    <label className="form-label">
      {localizationStrings?.timePeriod}
    </label>

    <select
      className="form-select"
      id="time"
      name="time"
      value={timePeriod}
      onChange={(e) => setTimePeriod(e.target.value)}
    >
      <option value="24h">
        {localizationStrings?.last24Hours}
      </option>

      <option value="7d">
        {localizationStrings?.last7Days}
      </option>

      {license === "Enterprise" && (
        <option value="30d">
          {localizationStrings?.last30Days}
        </option>
      )}
    </select>
  </div>

  {/* Network Name */}
  <div className="col-lg-4">
    <label className="form-label">
      {localizationStrings?.networkName}
    </label>

   <select
  className="form-select"
  value={networkId}
  onChange={(e) => setNetworkId(Number(e.target.value))}
>
  <option value="">
    {localizationStrings?.selectNetwork}
  </option>

  {networkIds.map((network) => (
    <option
      key={network.network_id}
      value={network.network_id}
    >
      {network.network_name}
    </option>
  ))}
</select>
  </div>
</div>

      <h4>
        {localizationStrings?.emailAddress}
        <span className="text-danger">*</span>
      </h4>
      <p>{localizationStrings?.profileEmailInfo}</p>
      <div className="row mb-35">
        <div className="col-lg-7">
          <input
            type="text"
            className="form-control"
            value={emailRecipients}
            onChange={handleEmailChange}
          />
          {emailError && <small className="text-danger">{emailError}</small>}
        </div>
      </div>

      <h4 className="mb-2">{localizationStrings?.brandingLogo}</h4>
      <div className="report-radio mb-4">
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="logo"
            id="logo1"
            value="Use VAR Logo"
            checked={organizationLogo === "Use VAR Logo"}
            onChange={() => setOrganizationLogo("Use VAR Logo")}
          />
          <label className="form-check-label" htmlFor="logo1">
            {localizationStrings?.useVarLogo}
          </label>
        </div>
        <div>
          <input
            className="form-check-input"
            type="radio"
            name="logo"
            id="logo2"
            value="Use Organization Logo"
            checked={organizationLogo === "Use Organization Logo"}
            onChange={() => setOrganizationLogo("Use Organization Logo")}
          />
          <label className="form-check-label" htmlFor="logo2">
            {localizationStrings?.useOrganizationLogo}
          </label>
        </div>
      </div>
    </div>
  );
}

export default ReportProfile;
